// vg-binding: P7.D-160
// vg-binding: P7.D-14
//
// apps/vendor/src/pages/ProductEdit.tsx
//
// Phase 7 Amendment #3 Task 84 — vendor product edit form at
// /products/:id/edit. Reuses CommonFields + VariantMatrix from the add
// wizard, prefilled from /products/:id detail data.
//
// Three edit modes:
//   • Non-Live (pending|rejected): straight PATCH, no re-approval.
//   • Live: warning banner + per-field warning icon when one of the 5
//           trigger fields diverges from the server snapshot.
//          BE (productEditService) handles ProductApproval
//          row creation on submit; FE just submits and lets the server
//          decide whether to flip state.
//   • Inactive / inactive_countdown: hard-redirect back to detail with
//          a toast — edits are blocked (no delete, must
//          reactivate first).

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useTranslation } from '../lib/i18n/index.js';
import {
  COMMON_FIELDS_DEFAULT,
  CommonFields,
  type CommonFieldsValue,
} from '../features/products/add/CommonFields.js';
import {
  cellKey,
  ensureCells,
  emptyMatrix,
  variantsFromMatrix,
  type MatrixModel,
  type VariantDraft,
} from '../features/products/variants/types.js';
import { VariantMatrix } from '../features/products/variants/VariantMatrix.js';
import { NativeSelect, PageHeader } from '@printway/ui';
import {
  usePrintwaySkus,
  useProductDetail,
  useUpdateProduct,
  type ProductDetailData,
  type ProductsApiError,
  type UpdateProductInput,
} from '../features/products/api.js';
import { hasPerSkuCost } from '../features/products/variantCost.js';

// product-level fields whose change triggers re-approval on a Live
// product. Bug 7 — `dimensions` removed (now per-variant). Amendment #4
// processing/capacity/weight removed (now per-variant); the BE
// flips the product to pending when any variant's ops/dimensions change.
// `cost_price_cents` is the only product-level trigger left.
const TRIGGER_FIELDS = ['cost_price_cents'] as const;

function commonFieldsFromDetail(data: ProductDetailData): CommonFieldsValue {
  return {
    name: data.product.name,
    sku_prefix: data.product.sku_prefix,
    product_type: data.product.product_type,
    cost_price_cents: data.product.cost_price_cents,
    // Bug 7 + Amendment #4 — dimensions + ops fields hydrated per-variant via
    // matrixFromDetail.
    low_stock_threshold: data.product.low_stock_threshold,
  };
}

function matrixFromDetail(data: ProductDetailData): MatrixModel {
  const sizesSet = new Set<string>();
  const colorsSet = new Set<string>();
  const sizes: string[] = [];
  const colors: string[] = [];
  for (const v of data.variants) {
    if (!sizesSet.has(v.size)) {
      sizesSet.add(v.size);
      sizes.push(v.size);
    }
    if (!colorsSet.has(v.color)) {
      colorsSet.add(v.color);
      colors.push(v.color);
    }
  }
  const cells: Record<string, VariantDraft> = {};
  for (const v of data.variants) {
    cells[cellKey(v.size, v.color)] = {
      size: v.size,
      color: v.color,
      enabled: v.enabled,
      cost_markup_cents: v.cost_markup_cents,
      ...(typeof v.cost_cents === 'number' ? { cost_cents: v.cost_cents } : {}),
      image_urls: v.image_urls,
      // Bug 7 — hydrate per-variant dimensions; default to 0s for legacy
      // variants that predate the migration backfill.
      dimensions: {
        length: v.dimensions?.length ?? 0,
        width: v.dimensions?.width ?? 0,
        height: v.dimensions?.height ?? 0,
      },
      // Amendment #4 (165) — hydrate per-SKU ops + external_sku.
      processing_time_days: v.processing_time_days ?? 0,
      capacity_per_day: v.capacity_per_day ?? 0,
      weight_grams: v.weight_grams ?? 0,
      external_sku: v.external_sku ?? '',
      // 2026-10-13 — keep the Printway mapping on the cell so the save
      // re-sends it (the server keys mapped rows on it, not on the label).
      ...(v.printway_variant_id ? { printway_variant_id: v.printway_variant_id, sku: v.sku ?? '' } : {}),
      // BE column default is null; VariantDraft.weight_grams_override is
      // typed as `number | undefined`. Coerce null → omit so the matrix
      // model stays type-honest and submit never carries a null forward.
      ...(typeof v.weight_grams_override === 'number'
        ? { weight_grams_override: v.weight_grams_override }
        : {}),
    };
  }
  return ensureCells({ sizes, colors, cells });
}

function isTriggerFieldChanged(
  field: (typeof TRIGGER_FIELDS)[number],
  current: CommonFieldsValue,
  snapshot: CommonFieldsValue,
): boolean {
  // Bug 7 — all remaining trigger fields are scalar product fields.
  return current[field] !== snapshot[field];
}


/** Edit errors with their own translated message (`edit.errors.<code>`). */
const EDIT_ERROR_CODES: ReadonlySet<string> = new Set([
  'PRINTWAY_MAPPING_IMMUTABLE',
  'ALREADY_LISTED',
  'MIXED_BLUEPRINT',
  'PRINTWAY_SKU_REQUIRED',
  'OUT_OF_RANGE',
  'REQUIRED',
]);

/**
 * R7-01 — `field_errors` of an OUT_OF_RANGE edit, keyed
 * `<field>@<size>__<color>`, regrouped per matrix cell (cellKey).
 */
function cellFieldErrors(fieldErrors: Record<string, string> | undefined): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const key of Object.keys(fieldErrors ?? {})) {
    const at = key.indexOf('@');
    if (at <= 0) continue;
    const field = key.slice(0, at);
    const cell = key.slice(at + 1);
    (out[cell] ??= []).push(field);
  }
  return out;
}

export function ProductEditPage(): JSX.Element {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const detailQ = useProductDetail(id);
  const updateM = useUpdateProduct();

  const [commonFields, setCommonFields] = useState<CommonFieldsValue>(
    COMMON_FIELDS_DEFAULT,
  );
  const [matrix, setMatrix] = useState<MatrixModel>(ensureCells(emptyMatrix()));
  const [snapshot, setSnapshot] = useState<CommonFieldsValue | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [cellErrors, setCellErrors] = useState<Record<string, string[]>>({});
  const [hydrated, setHydrated] = useState(false);

  // Hydrate form once when detail loads.
  useEffect(() => {
    if (detailQ.data && !hydrated) {
      const cf = commonFieldsFromDetail(detailQ.data);
      setCommonFields(cf);
      setSnapshot(cf);
      setMatrix(matrixFromDetail(detailQ.data));
      setHydrated(true);
    }
  }, [detailQ.data, hydrated]);

  // Inactive states hard-redirect to detail.
  useEffect(() => {
    if (
      detailQ.data &&
      (detailQ.data.product.state === 'inactive' ||
        detailQ.data.product.state === 'inactive_countdown')
    ) {
      // No toast lib wired in vendor portal yet — fall back to alert so the
      // user knows why they were bounced. Phase 7 wave 18 may add a toast.
      window.alert(t('edit.inactiveRedirect'));
      navigate(`/products/${detailQ.data.product._id}`, { replace: true });
    }
  }, [detailQ.data, navigate, t]);

  const triggerFieldsChanged = useMemo(() => {
    if (!snapshot) return new Set<string>();
    const changed = new Set<string>();
    for (const f of TRIGGER_FIELDS) {
      if (isTriggerFieldChanged(f, commonFields, snapshot)) changed.add(f);
    }
    return changed;
  }, [commonFields, snapshot]);

  if (detailQ.isLoading || !hydrated) {
    return (
      <main className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary" />
          <p className="text-sm text-pw-text-muted">{t('edit.loading')}</p>
        </div>
      </main>
    );
  }

  if (detailQ.isError || !detailQ.data) {
    return (
      <main className="px-6 py-10">
        <div className="mx-auto max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 text-center shadow-sm">
          <h2 className="text-lg font-semibold text-pw-text">
            {t('detail.notFoundTitle')}
          </h2>
          <Link
            to="/products"
            className="mt-4 inline-flex rounded-md bg-pw-primary px-4 py-2 text-sm font-medium text-white hover:bg-pw-primary-strong"
          >
            {t('detail.backToList')}
          </Link>
        </div>
      </main>
    );
  }

  const { product } = detailQ.data;
  const isLive = product.state === 'live';
  // A product imported against a Printway blueprint: only mapped cells are
  // real variants; the matrix's size × color fill-ins are not sent.
  const mapped = detailQ.data.variants.some((v) => !!v.printway_variant_id);
  // D16a / D17 — priced per SKU (file import): each variant carries its own
  // cost; the product cost and the markup are locked, a new variant must be
  // given a price.
  const perSku = hasPerSkuCost(detailQ.data.variants);

  async function handleSubmit(): Promise<void> {
    setErrorMsg(null);
    setCellErrors({});
    // UAT #2126 — sản phẩm live đổi trigger field (giá gốc) phải có cảnh
    // báo TRƯỚC khi lưu; banner inline dễ bị lướt qua nên chặn thêm bằng
    // confirm liệt kê các trường đã đổi.
    if (isLive && triggerFieldsChanged.size > 0) {
      const ok = window.confirm(
        `${t('edit.triggerFieldChanged')}: ${[...triggerFieldsChanged].join(', ')}\n\n${t('edit.liveBanner.body')}`,
      );
      if (!ok) return;
    }
    try {
      // dbg-r3 — variants are sent to the BE which upserts on (size, color).
      // Missing variants are NOT deleted; removal needs a separate flow.
      const payload: UpdateProductInput = {
        product_id: product._id,
        version: product.version,
        name: commonFields.name,
        sku_prefix: commonFields.sku_prefix,
        product_type: commonFields.product_type,
        // D16a — the server derives it from the per-SKU prices; never sent then.
        ...(perSku ? {} : { cost_price_cents: commonFields.cost_price_cents }),
        // Bug 7 + Amendment #4 — dimensions + per-SKU ops sent per-variant below.
        low_stock_threshold: commonFields.low_stock_threshold,
        variants: variantsFromMatrix(matrix)
          .filter((v) => !mapped || !!v.printway_variant_id)
          .map((v) => ({
          size: v.size,
          color: v.color,
          enabled: v.enabled,
          // D17 — per-SKU product: the cell's own cost, no markup.
          ...(perSku ? { cost_cents: v.cost_cents ?? null } : { cost_markup_cents: v.cost_markup_cents }),
          image_urls: v.image_urls.filter((u) => u.trim().length > 0),
          dimensions: v.dimensions,
          // Amendment #4 — per-SKU ops fields.
          processing_time_days: v.processing_time_days,
          capacity_per_day: v.capacity_per_day,
          weight_grams: v.weight_grams,
          // Amendment #4 — external_sku (omit empty).
          ...(v.external_sku && v.external_sku.trim().length > 0
            ? { external_sku: v.external_sku.trim() }
            : {}),
          // BE Zod expects `number` when present. Existing DB variants
          // come back with `weight_grams_override: null` (column default),
          // so the old `!== undefined` check let null leak through and
          // tripped Zod with "Expected number, received null". Filter to
          // numeric values only.
          ...(typeof v.weight_grams_override === 'number'
            ? { weight_grams_override: v.weight_grams_override }
            : {}),
          ...(v.printway_variant_id ? { printway_variant_id: v.printway_variant_id } : {}),
        })),
      };
      await updateM.mutateAsync(payload);
      navigate(`/products/${product._id}`);
    } catch (err) {
      const e = err as ProductsApiError;
      // Mapping rules the server enforces (D1/D5): a translated reason, not
      // the English API message.
      const known = e.code && EDIT_ERROR_CODES.has(e.code) ? t(`edit.errors.${e.code}`) : null;
      if (e.code === 'OUT_OF_RANGE' || e.code === 'REQUIRED') setCellErrors(cellFieldErrors(e.fieldErrors));
      setErrorMsg(known ?? e.userMessage ?? e.message ?? t('edit.submitFailed'));
    }
  }

  return (
    <main
      data-testid="product-edit-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader
        title={t('edit.pageTitle')}
        actions={
          <Link
            to={`/products/${product._id}`}
            className="text-sm text-pw-primary hover:underline"
          >
            {t('edit.backToDetail')}
          </Link>
        }
      />

      {isLive ? (
        <div
          data-testid="product-edit-live-banner"
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <p className="font-semibold">{t('edit.liveBanner.title')}</p>
          <p className="mt-1">{t('edit.liveBanner.body')}</p>
          <p className="mt-1 text-xs italic text-amber-800">
            {t('edit.liveBanner.triggerFieldsLabel')}
          </p>
        </div>
      ) : null}

      <section className="rounded-xl border border-pw-border bg-pw-bg p-4">
        <h3 className="mb-3 text-sm font-semibold text-pw-text">
          {t('detail.commonInfoHeader')}
        </h3>
        <CommonFields value={commonFields} onChange={setCommonFields} costLocked={perSku} />
        {isLive && triggerFieldsChanged.size > 0 ? (
          <p
            data-testid="product-edit-trigger-warning"
            className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            ⚠ {t('edit.triggerFieldChanged')}: {[...triggerFieldsChanged].join(', ')}
          </p>
        ) : null}
      </section>

      {/* dbg-r3 — variants are now upserted server-side on (size, color).
          Removing a row from the matrix does NOT delete the variant on
          the server yet — that's an explicit separate flow to avoid
          orphaning the vendor_inventory_stock rows tied to variant_id. */}
      <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
        ℹ Thêm size/màu/giá markup sẽ lưu khi bấm Save. Xoá variant khỏi
        matrix hiện chưa xoá ở DB (giữ tồn kho liên kết) — cần admin xử lý
        riêng nếu muốn xoá hẳn.
      </div>
      {mapped ? (
        <AddPrintwaySku
          blueprintId={product.source_id ?? undefined}
          taken={new Set(variantsFromMatrix(matrix).map((v) => v.printway_variant_id).filter(Boolean) as string[])}
          onAdd={(sku) =>
            setMatrix((m) => {
              const sizes = m.sizes.includes(sku.size || 'Default') ? m.sizes : [...m.sizes, sku.size || 'Default'];
              const colors = m.colors.includes(sku.color || 'Default') ? m.colors : [...m.colors, sku.color || 'Default'];
              const key = cellKey(sku.size || 'Default', sku.color || 'Default');
              const base = ensureCells({ sizes, colors, cells: m.cells });
              return {
                ...base,
                cells: {
                  ...base.cells,
                  [key]: { ...base.cells[key]!, enabled: true, printway_variant_id: sku.variant_id, sku: sku.sku },
                },
              };
            })
          }
        />
      ) : null}
      <VariantMatrix value={matrix} onChange={setMatrix} fieldErrors={cellErrors} perSku={perSku} />

      {errorMsg ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMsg}
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          data-testid="product-edit-cancel"
          onClick={() => navigate(`/products/${product._id}`)}
          className="inline-flex h-10 items-center rounded-md border border-pw-border bg-pw-surface px-4 text-sm font-medium text-pw-text hover:bg-pw-bg"
        >
          {t('edit.cancel')}
        </button>
        <button
          type="button"
          data-testid="product-edit-submit"
          disabled={updateM.isPending}
          onClick={() => void handleSubmit()}
          className="inline-flex h-10 items-center rounded-md bg-pw-primary px-5 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
        >
          {updateM.isPending ? t('edit.submitting') : t('edit.submit')}
        </button>
      </div>
    </main>
  );
}

/**
 * 2026-10-13 — add a variant to a mapped product by picking an unlisted
 * Printway SKU of the SAME blueprint (the server refuses anything else).
 */
function AddPrintwaySku({
  blueprintId,
  taken,
  onAdd,
}: {
  blueprintId: string | undefined;
  taken: Set<string>;
  onAdd: (sku: { variant_id: string; sku: string; size: string; color: string }) => void;
}): JSX.Element {
  const { t } = useTranslation('products');
  const q = usePrintwaySkus({ ...(blueprintId ? { blueprintId } : {}), unlisted: true, enabled: !!blueprintId });
  const options = (q.data ?? []).filter((s) => !taken.has(s.variant_id));
  return (
    <section className="rounded-xl border border-pw-border bg-pw-bg p-4" data-testid="product-edit-add-pw-sku">
      <p className="mb-2 text-xs text-pw-text-muted">{t('edit.mappedHint')}</p>
      <NativeSelect
        value=""
        disabled={q.isLoading || options.length === 0}
        aria-label={t('edit.addPrintwaySku')}
        className="w-full max-w-md"
        onChange={(e) => {
          const pick = options.find((o) => o.variant_id === e.target.value);
          if (pick) onAdd(pick);
        }}
        data-testid="product-edit-add-pw-sku-select"
      >
        <option value="">
          {q.isLoading ? t('edit.loadingSkus') : options.length === 0 ? t('edit.noSkuLeft') : t('edit.addPrintwaySku')}
        </option>
        {options.map((o) => (
          <option key={o.variant_id} value={o.variant_id}>
            {o.sku} — {[o.size, o.color].filter(Boolean).join(' / ')}
          </option>
        ))}
      </NativeSelect>
    </section>
  );
}

export default ProductEditPage;
